import { Card } from '@/types/Card';
import { CARD_KEYS, BANK_KEYS, BANK_NAMES } from '../../constants/keys';
import { FeatureType } from '@/types/Feature';
import { CardLinkMapping, BankLinkMapping } from '../cardLinks';

/**
 * Map of bank keys to their source URLs
 * This allows us to easily update bank information when they change their websites
 * The key is the bank key from BANK_KEYS
 */
export const bankLinkMap: Record<string, BankLinkMapping> = {
  [BANK_KEYS.CTBC]: {
    sourceUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index.html',
    lastUpdated: '2025-06-21',
  },
};

/**
 * Map of card keys to their source URLs
 * This allows us to easily update card information when banks change their offerings
 * The key is the card key from CARD_KEYS
 */
export const cardLinkMap: Record<string, CardLinkMapping> = {
  // CTBC Bank cards
  [CARD_KEYS.CTBC_LINEPAY]: {
    sourceUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_LINEPay.html',
    applyUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_LINEPay.html',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.CTBC_TITANIUM]: {
    sourceUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/B_EP_Titanium.html',
    applyUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/B_EP_Titanium.html',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.CTBC_CPCYOYO]: {
    sourceUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_CPC.html',
    applyUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_CPC.html',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.CTBC_LALAPORT]: {
    sourceUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_LaLaport.html',
    applyUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_LaLaport.html',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.CTBC_AGODA]: {
    sourceUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_Agoda.html',
    applyUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_Agoda.html',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.CTBC_ANA]: {
    sourceUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_ANA.html',
    applyUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_ANA.html',
    lastUpdated: '2025-06-21',
  },
  [CARD_KEYS.CTBC_CHINA_AIRLINES]: {
    sourceUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_CAL.html',
    applyUrl:
      'https://www.ctbcbank.com/twrbo/zh_tw/cc_index/cc_product/cc_introduction_index/C_CAL.html',
    lastUpdated: '2025-06-21',
  },
};

export const ctbcCards: { [key: string]: Card } = {
  [CARD_KEYS.CTBC_ANA]: {
    name: '中信ANA聯民卡',
    summary: '日本消費最高6%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.CTBC],
    payment_network: 'VISA',
    picture: 'logo_ctbc-ana.png',
    key: 'ctbc-ana',
    link: cardLinkMap[CARD_KEYS.CTBC_ANA].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CTBC_ANA].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '6% 里数回饋',
        max_feedback: '每月最高回饋600里',
        simplified_condition: '日本消費',
        condition: '登錄后於日本實體通路消費享6%里数回饋，額外5%部分每月最高回饋600里',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3% 里数回饋',
        max_feedback: '每月最高回饋300里',
        simplified_condition: '國外消費',
        condition: '國外一般消費享3%里数回饋，額外2%部分每月最高回饋300里',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 里数回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%里数回饋，無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '3%',
    specific_channel_feedback: '6%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.CTBC_CHINA_AIRLINES]: {
    name: '中信華航聯名卡',
    summary: '華航購票最高7%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.CTBC],
    payment_network: 'MASTERCARD',
    picture: 'logo_ctbc-china-airlines.png',
    key: 'ctbc-china-airlines',
    link: cardLinkMap[CARD_KEYS.CTBC_CHINA_AIRLINES].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CTBC_CHINA_AIRLINES].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '7% 里數回饋',
        max_feedback: '每月最高回饋700里',
        simplified_condition: '華航購票',
        condition: '於華航官網購票享7%里數回饋，額外6%部分每月最高回饋700里',
        channel: '旅遊',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.5% 里數回饋',
        max_feedback: '每月最高回饋250里',
        simplified_condition: '國外消費',
        condition: '國外一般消費享2.5%里數回饋，額外1.5%部分每月最高回饋250里',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 里數回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%里數回饋，無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '2.5%',
    specific_channel_feedback: '7%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.CTBC_AGODA]: {
    name: '中信Agoda聯名卡',
    summary: 'Agoda訂房最高7%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.CTBC],
    payment_network: 'VISA',
    picture: 'logo_ctbc-agoda.png',
    key: 'ctbc-agoda',
    link: cardLinkMap[CARD_KEYS.CTBC_AGODA].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CTBC_AGODA].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '7% 回饋',
        max_feedback: '每月最高回饋500元',
        simplified_condition: 'Agoda訂房',
        condition: '於Agoda官網或APP訂房享7%回饋，額外6%部分每月最高回饋500元',
        channel: '旅遊',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.5% 回饋',
        max_feedback: '每月最高回饋300元',
        simplified_condition: '國外消費',
        condition: '國外一般消費享2.5%回饋，額外1.5%部分每月最高回饋300元',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%現金回饋，無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '2.5%',
    specific_channel_feedback: '7%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.CTBC_LALAPORT]: {
    name: '中信三井LaLaport聯名卡',
    summary: '指定通路最高10%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.CTBC],
    payment_network: 'MASTERCARD',
    picture: 'logo_ctbc-lalaport.png',
    key: 'ctbc-lalaport',
    link: cardLinkMap[CARD_KEYS.CTBC_LALAPORT].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CTBC_LALAPORT].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '10% 回饋',
        max_feedback: '每月最高回饋500元',
        simplified_condition: 'LaLaport指定通路',
        condition: '於三井LaLaport指定通路消費享10%回饋，額外9%部分每月最高回饋500元',
        channel: '百貨',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 回饋',
        max_feedback: '每月最高回饋300元',
        simplified_condition: '指定網購通路',
        condition: '於蝦皮購物、momo購物網、淘寶網消費享5%回饋，額外4%部分每月最高回饋300元',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%現金回饋，無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '10%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.CTBC_CPCYOYO]: {
    name: '中信中油聯名卡',
    summary: '中油直營站最高6.8%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.CTBC],
    payment_network: 'VISA',
    picture: 'logo_ctbc-cpcyoyo.png',
    key: 'ctbc-cpcyoyo',
    link: cardLinkMap[CARD_KEYS.CTBC_CPCYOYO].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CTBC_CPCYOYO].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '6.8% 回饋',
        max_feedback: '每月最高回饋300元',
        simplified_condition: '中油直營站',
        condition: '使用中信Pay綁定捷利卡於中油直營站享6.8%回饋，額外5.8%部分每月最高回饋300元',
        channel: '加油',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '3.8% 回饋',
        max_feedback: '每月最高回饋300元',
        simplified_condition: '中油加盟站',
        condition: '使用中信Pay綁定捷利卡於中油加盟站享3.8%回饋，額外2.8%部分每月最高回饋300元',
        channel: '加油',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '一般消費',
        condition: '一般消費享1%現金回饋，無上限',
        channel: '一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '1%',
    specific_channel_feedback: '6.8%',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
  },
  [CARD_KEYS.CTBC_LINEPAY]: {
    name: '中信LINE Pay信用卡',
    summary: '日韓新泰實體5%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.CTBC],
    payment_network: 'VISA',
    picture: 'logo_ctbc-linepay.png',
    key: 'ctbc-linepay',
    link: cardLinkMap[CARD_KEYS.CTBC_LINEPAY].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CTBC_LINEPAY].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% LINE Points',
        max_feedback: '每月最高回饋450點',
        simplified_condition: '日韓新泰實體消費',
        condition:
          '登錄後於日本、韓國、泰國、新加坡實體通路消費享5% LINE Points回饋，額外2.2%部分最高回饋450點/月',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.8% LINE Points',
        max_feedback: '回饋無上限',
        simplified_condition: '國外消費',
        condition: '國外一般消費享2.8% LINE Points回饋，無上限',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% LINE Points',
        max_feedback: '回饋無上限',
        simplified_condition: '國內消費',
        condition: '國內一般消費享1% LINE Points回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '2.8%',
    specific_channel_feedback: '5%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
  [CARD_KEYS.CTBC_TITANIUM]: {
    name: '中信商旅鈦金卡',
    summary: '國外2.5%、指定亞太國家5%回饋',
    issuing_bank: BANK_NAMES[BANK_KEYS.CTBC],
    payment_network: 'MASTERCARD',
    picture: 'logo_ctbc-titanium.png',
    key: 'ctbc-titanium',
    link: cardLinkMap[CARD_KEYS.CTBC_TITANIUM].sourceUrl,
    apply_link: cardLinkMap[CARD_KEYS.CTBC_TITANIUM].applyUrl,
    features: [
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 現金回饋',
        max_feedback: '每月最高回饋300元',
        simplified_condition: '指定亞太國家實體消費',
        condition:
          '登錄後於亞太地區指定國家實體消費享5%回饋，額外2.5%部分最高回饋300元/月，每月最高可刷12,000元',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '5% 現金回饋',
        max_feedback: '每月最高回饋300元',
        simplified_condition: '指定網購消費',
        condition:
          '登錄後於蝦皮購物、momo購物網、淘寶網、Coupang酷澎消費享5%回饋，額外4%部分最高回饋300元/月',
        channel: '網購',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '2.5% 現金回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '國外消費',
        condition: '國外一般消費享2.5%現金回饋，無上限',
        channel: '海外消費',
      },
      {
        type: FeatureType.CASH_BACK,
        feedback: '1% 現金回饋',
        max_feedback: '回饋無上限',
        simplified_condition: '國內消費',
        condition: '國內一般消費享1%現金回饋，無上限',
        channel: '國內一般消費',
      },
    ],
    domestic_feedback: '1%',
    international_feedback: '2.5%',
    specific_channel_feedback: '5%',
    start_date: '2025-01-01',
    end_date: '2025-06-30',
  },
};
